import { useState, useEffect, useCallback } from 'react'
import storage from '@/utils/storage'
import type { LineOption } from '@/utils/storage'
import preferencesApi from '@/utils/api/preferencesApi'
import { preferredLinesKey, defaultLineKey } from '@/utils/preferenceKeys'

interface UseLineOnboardingOptions {
    /** The space the onboarding is for: the lines, the answer of the API and the done flag are the ones of this space */
    spaceId: string
    endpoint: string
    getApiToken: () => Promise<string>
    preferencesApiEnabled?: boolean
}

interface UseLineOnboardingResult {
    isCheckingPreferences: boolean
    needsOnboarding: boolean
    isSaving: boolean
    saveError: string | null
    completeOnboarding: (preferredLines: LineOption[]) => Promise<void>
    dismissOnboarding: () => void
    clearSaveError: () => void
}

export function useLineOnboarding({
    spaceId,
    endpoint,
    getApiToken,
    preferencesApiEnabled = false,
}: UseLineOnboardingOptions): UseLineOnboardingResult {
    const [isCheckingPreferences, setIsCheckingPreferences] = useState(true)
    const [needsOnboarding, setNeedsOnboarding] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [saveError, setSaveError] = useState<string | null>(null)

    useEffect(() => {
        // Config not ready yet (can't reach the API), or no space to ask for.
        if (!endpoint || !spaceId) return

        // Fast path: already completed in a previous session.
        if (storage.getOnboardingCompleted(spaceId)) {
            setIsCheckingPreferences(false)
            return
        }

        // Without the preferences API there is nothing to fetch — show onboarding.
        if (!preferencesApiEnabled) {
            setNeedsOnboarding(true)
            setIsCheckingPreferences(false)
            return
        }

        // Call the API regardless of whether line metadata has loaded yet —
        // "does the user have saved preferences?" is independent of the available-
        // lines list, which is only needed to render the selection UI.
        let cancelled = false
        let timeoutId: NodeJS.Timeout | null = null

        const checkPreferences = async () => {
            try {
                const token = await getApiToken()
                const preferences = await preferencesApi.getPreferences(endpoint, token)
                if (!cancelled) {
                    const preferred = preferences[preferredLinesKey(spaceId)]
                    const hasPreferred = Array.isArray(preferred) && preferred.length > 0
                    if (hasPreferred) storage.setOnboardingCompleted(true, spaceId)
                    setNeedsOnboarding(!hasPreferred)
                }
            } catch {
                // On API error, show onboarding — localStorage fast-paths above already
                // protect users who have previously completed it.
                if (!cancelled) setNeedsOnboarding(true)
            } finally {
                if (timeoutId) clearTimeout(timeoutId)
                if (!cancelled) setIsCheckingPreferences(false)
            }
        }

        // 8-second timeout: if preference checking hangs, force completion
        timeoutId = setTimeout(() => {
            if (!cancelled) {
                cancelled = true
                console.warn('Line preference check timed out — proceeding without server preferences')
                setNeedsOnboarding(true)
                setIsCheckingPreferences(false)
            }
        }, 8000)

        checkPreferences()

        return () => {
            cancelled = true
            if (timeoutId) clearTimeout(timeoutId)
        }
    }, [spaceId, endpoint, preferencesApiEnabled, getApiToken])

    const completeOnboarding = useCallback(
        async (preferredLines: LineOption[]) => {
            setSaveError(null)
            setIsSaving(true)
            try {
                if (preferencesApiEnabled) {
                    const token = await getApiToken()
                    await preferencesApi.patchPreferences(endpoint, token, {
                        [preferredLinesKey(spaceId)]: preferredLines.map((l) => l.value),
                        [defaultLineKey(spaceId)]: preferredLines[0]?.value ?? null,
                    })
                }
                storage.setPreferredLines(preferredLines, spaceId)
                storage.setDefaultLine(preferredLines[0]?.value ?? null, spaceId)
                storage.setOnboardingCompleted(true, spaceId)
                setNeedsOnboarding(false)
            } catch {
                setSaveError('saveError')
            } finally {
                setIsSaving(false)
            }
        },
        [spaceId, endpoint, getApiToken, preferencesApiEnabled]
    )

    const dismissOnboarding = useCallback(() => {
        setNeedsOnboarding(false)
    }, [])

    const clearSaveError = useCallback(() => {
        setSaveError(null)
    }, [])

    return {
        isCheckingPreferences,
        needsOnboarding,
        isSaving,
        saveError,
        completeOnboarding,
        dismissOnboarding,
        clearSaveError,
    }
}
